'use client';

import { useRef, type ReactNode } from 'react';
import { useInView } from './use-in-view';
import styles from './load-more.module.css';

/**
 * LoadMore - the quiet end of a list that holds more than it shows
 * (docs/DESIGN_SYSTEM.md "Component library"): one button in the faint voice
 * that says how much is left ("12 more"). It loads more when the reader
 * reaches it, while it is in the viewport (`useInView`), and on a press, which
 * stays the way in for the keyboard and for a reader who asked for less
 * motion (`prefers-reduced-motion` turns the loading on reach off). While a
 * load is in flight (`busy`) neither starts another.
 *
 * Not an anchor of the scroll (`overflow-anchor: none`): rows land above it,
 * so the reader stays where they were and the button moves down.
 */
export function LoadMore({
  children,
  label,
  onLoad,
  busy = false,
  auto = true,
  className,
  testId,
}: {
  /** The visible words: how much is left. */
  children: ReactNode;
  /** The accessible name, when the words alone do not say what loads. */
  label?: string;
  onLoad: () => void;
  /** A load is in flight. */
  busy?: boolean;
  /** Load on reach (the default); off, only a press loads. */
  auto?: boolean;
  className?: string;
  testId?: string;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  useInView(ref, onLoad, auto && !busy);
  return (
    <button
      ref={ref}
      type="button"
      className={className ? `${styles.more} ${className}` : styles.more}
      aria-label={label}
      aria-busy={busy || undefined}
      onClick={() => {
        if (!busy) onLoad();
      }}
      data-testid={testId}
    >
      {children}
    </button>
  );
}
